import type { CreatedByType, ModuleItemType } from 'app/types/modules';

/** Destino de la copia dentro del programa actual. */
export type CopyModuleTarget = {
  /** Autoría del módulo copiado: quien copia, no quien creó el original. */
  createdBy: CreatedByType;
  evaluationPeriodId: string;
  order: number;
};

let copyCounter = 0;

/**
 * Copia **local** del módulo elegido al programa actual. Devuelve un módulo nuevo,
 * sin vínculo con el de origen: guids propios (submódulos incluidos, re-parentados),
 * asignado al periodo destino, oculto por defecto (`visibility: 0` y `status: 'draft'`,
 * la misma visibilidad inicial que aplica `createModule` a un módulo nuevo) y con la
 * autoría de quien copia — el original puede ser de contenido editorial, y `itemActions`
 * decide los permisos de fila por `created_by`, así que heredarlo dejaría la copia sin
 * editar ni borrar para su propietario.
 *
 * Los submódulos se copian con un spread superficial: basta porque el resto de campos
 * son primitivos y el consumidor no muta el original, pero `created_by` se reemplaza
 * explícitamente en vez de compartir la referencia del origen.
 *
 * Marca `is_temporary: true` en el módulo y sus submódulos: el guid no existe en
 * backend, así que las filas no pueden ofrecer acciones ni entrar en el payload de
 * reordenación hasta que la copia se persista de verdad.
 *
 * Siempre resuelve: el camino de error se simula en tests rechazando esta función.
 * Pendiente TAN-10196: sustituir por la llamada real al endpoint de copia (el listado
 * de cursos y módulos de origen ya es real desde TAN-10192).
 */
export async function copyModuleLocally(sourceModule: ModuleItemType, target: CopyModuleTarget): Promise<ModuleItemType> {
  copyCounter += 1;

  const copiedGuid = `${sourceModule.guid}-copy-${copyCounter}`;

  return {
    ...sourceModule,
    guid: copiedGuid,
    created_by: target.createdBy,
    evaluation_period_id: target.evaluationPeriodId,
    is_temporary: true,
    order: target.order,
    status: 'draft',
    visibility: 0,
    items: sourceModule.items?.map((submodule) => ({
      ...submodule,
      guid: `${submodule.guid}-copy-${copyCounter}`,
      created_by: target.createdBy,
      parent_guid: copiedGuid,
      evaluation_period_id: target.evaluationPeriodId,
      is_temporary: true,
      status: 'draft',
      visibility: 0,
    })),
  };
}
